import React from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Field, Input, Message, RadioGroup, Textarea } from '@workwhile/ui'
import { ShiftWorkBreak } from 'api/shifts/getShiftWorkBreaks'
import {
  BreakAction,
  RecordShiftWorkBreakRequest,
} from 'api/shifts/recordShiftWorkBreak'
import { addMinutes, startOfMinute } from 'date-fns'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { EditBreakSchema, getEditBreakSchema } from './editBreakSchema'

export const EDIT_BREAK_FORM_ID = 'edit-break-form'

const DATETIME_LOCAL_FORMAT = "yyyy-MM-dd'T'HH:mm"

const ACTION_BY_STATUS: Record<
  NonNullable<ShiftWorkBreak['status']>,
  BreakAction
> = {
  taking: 'take',
  taken: 'take',
  waived: 'waive',
  missed: 'missed',
}

const toLocalInput = (value: string | null | undefined, timezone: string) =>
  value ? formatInTimeZone(value, timezone, DATETIME_LOCAL_FORMAT) : ''

const toUtcIso = (value: string, timezone: string) =>
  fromZonedTime(value, timezone).toISOString()

const ceilToMinute = (value: string) => {
  const date = new Date(value)
  const floored = startOfMinute(date)
  return (
    floored.getTime() === date.getTime() ? floored : addMinutes(floored, 1)
  ).toISOString()
}

type EditBreakFormProps = {
  shiftWorkBreak: ShiftWorkBreak
  timezone: string
  clockedInAt?: string | null
  clockedOutAt?: string | null
  onSubmit: (request: RecordShiftWorkBreakRequest) => void
  errorMessage?: string
}

export const EditBreakForm = ({
  shiftWorkBreak,
  timezone,
  clockedInAt,
  clockedOutAt,
  onSubmit,
  errorMessage,
}: EditBreakFormProps) => {
  const minTime = clockedInAt
    ? toLocalInput(ceilToMinute(clockedInAt), timezone)
    : undefined
  const maxTime = clockedOutAt
    ? toLocalInput(clockedOutAt, timezone)
    : undefined
  const takeAfter = toLocalInput(shiftWorkBreak.takeAfter, timezone)
  const suggestedStart =
    minTime && takeAfter > minTime ? takeAfter : minTime ?? ''
  const suggestedEnd = suggestedStart
    ? toLocalInput(
        addMinutes(
          fromZonedTime(suggestedStart, timezone),
          shiftWorkBreak.durationInMinutes
        ).toISOString(),
        timezone
      )
    : ''
  const defaultStart = shiftWorkBreak.startTime
    ? toLocalInput(shiftWorkBreak.startTime, timezone)
    : suggestedStart
  const defaultEnd = shiftWorkBreak.endTime
    ? toLocalInput(shiftWorkBreak.endTime, timezone)
    : maxTime && suggestedEnd > maxTime
      ? maxTime
      : suggestedEnd
  const canWaive = shiftWorkBreak.waivePolicy === 'allowed'
  const statusAction = shiftWorkBreak.status
    ? ACTION_BY_STATUS[shiftWorkBreak.status]
    : undefined
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EditBreakSchema>({
    resolver: zodResolver(
      getEditBreakSchema({ clockedInAt: minTime, clockedOutAt: maxTime })
    ),
    defaultValues: {
      action:
        !statusAction || (statusAction === 'waive' && !canWaive)
          ? 'take'
          : statusAction,
      startTime: defaultStart,
      endTime: defaultEnd,
      reason: shiftWorkBreak.reason ?? '',
    },
  })
  const action = useWatch({ control, name: 'action' })
  const startTime = useWatch({ control, name: 'startTime' })
  const endMinTime = startTime
    ? toLocalInput(
        addMinutes(fromZonedTime(startTime, timezone), 1).toISOString(),
        timezone
      )
    : minTime
  const allowReason = (selected: BreakAction) => selected === 'missed'

  const actionOptions = [
    { value: 'take', label: 'Taken' },
    ...(canWaive ? [{ value: 'waive', label: 'Waived' }] : []),
    { value: 'missed', label: 'Missed' },
  ]

  const submit = handleSubmit((data) => {
    if (data.action === 'take') {
      onSubmit({
        event: 'take',
        startTime: toUtcIso(data.startTime, timezone),
        endTime: toUtcIso(data.endTime, timezone),
      })
      return
    }
    const reason = allowReason(data.action)
      ? data.reason.trim() || undefined
      : undefined
    onSubmit({ event: data.action, reason })
  })

  return (
    <Box
      as="form"
      id={EDIT_BREAK_FORM_ID}
      aria-label="Edit break"
      onSubmit={submit}
    >
      <Controller
        name="action"
        control={control}
        render={({ field }) => (
          <Field label="Action">
            <RadioGroup
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              options={actionOptions}
            />
          </Field>
        )}
      />
      {action === 'take' ? (
        <>
          <Field label="Start time" error={errors.startTime?.message}>
            <Input
              block
              type="datetime-local"
              step={60}
              aria-label="Start time"
              min={minTime}
              max={maxTime}
              {...register('startTime')}
            />
          </Field>
          <Field label="End time" error={errors.endTime?.message}>
            <Input
              block
              type="datetime-local"
              step={60}
              aria-label="End time"
              min={endMinTime}
              max={maxTime}
              {...register('endTime')}
            />
          </Field>
        </>
      ) : null}
      {allowReason(action) ? (
        <Field
          label="Why couldn't the worker take this break?"
          notification="Leave blank if the worker chose to skip it."
        >
          <Textarea aria-label="Reason" minRows={2} {...register('reason')} />
        </Field>
      ) : null}
      {errorMessage ? (
        <Message variant="error" mt={3} borderRadius="small">
          {errorMessage}
        </Message>
      ) : null}
    </Box>
  )
}
